import React, { Component } from 'react';
import { Link } from 'react-router-dom';

import SidebarComponent from '../components/SidebarComponent';
import NavbarComponent from '../components/NavbarComponent';
import HeaderComponent from '../components/HeaderComponent';
import PaginationComponent from '../components/PaginationComponent';
import StockCountPaceComponent from '../components/StockCountPaceComponent';
import { validateSession } from '../utils/functions';
import { navigateTo } from '../utils/navigation';
import { isAdmin } from '../utils/enums';
import { formatEventDateTime } from '../utils/formats';
import { buildPaginationQuery, scheduleDebouncedCallback, clearDebouncedCallback } from '../utils/pagination';

// Panel solo-admin: control de conteos (resultado, intentos, por qué se eligió), cobertura
// y resolución de discrepancias. La config vive en Configuración > Conteo.
const config = process.env;
const apiBase = () => config.REACT_APP_BASE_URL + config.REACT_APP_VERSION;
const authHeaders = () => ({
    'access-token': localStorage.getItem('token'),
    'Content-Type': 'application/json',
});

const RESULT = {
    1: { label: 'Coincide', cls: 'badge-success' },
    2: { label: 'Discrepancia', cls: 'badge-danger' },
};
const REASON = {
    riesgo_sobreventa: { label: 'Riesgo sobreventa', cls: 'badge-danger' },
    rotacion: { label: 'Rotación', cls: 'badge-info' },
    vendido: { label: 'Vendido', cls: 'badge-primary' },
    repuesto: { label: 'Repuesto', cls: 'badge-secondary' },
    nunca_contado: { label: 'Nunca contado', cls: 'badge-warning' },
    problematico: { label: 'Problemático', cls: 'badge-dark' },
};

class StockCountAdminPage extends Component {
    constructor() {
        super();
        this.state = {
            // Arranca en false aunque haya token (a diferencia del resto de las pantallas):
            // el `role` cacheado lo escribe el propio browser, así que acá no se pinta nada
            // hasta que el backend confirme quién es. Si no, el encargado que escribe la URL
            // llega a ver el shell del control antes de que lo saquen.
            checked: false,
            role: Number(localStorage.getItem('role') || 0),
            counts: [],
            coverage: null,
            onlyDiscrepancies: false,
            // El listado trae 100 filas por página: sin paginado, "no puedo ver todo" era
            // literal — el resto de los conteos existía y la pantalla no tenía cómo pedirlos.
            currentPage: 1,
            quantityShowRows: 100,
            totalItems: 0,
            searchInput: '',
            search: '',
            // Se cuenta en el servidor. Contando sobre `counts` diría "las pendientes que hay
            // en esta página", que con paginado es un número distinto en cada página.
            pendientes: 0,
            // Resolución en curso: qué fila y con qué acción. La confirmación es un paso
            // aparte porque ahora pide el motivo (ver `renderResolveForm`).
            resolving: null, // { key, action }
            resolveNote: '',
            saving: false,
        };
    }

    async componentDidMount() {
        const role = await validateSession(true);
        if (!role) {
            return; // sesión vencida/inválida: validateSession ya sacó al usuario.
        }

        // El control es admin-only (backend: /stock/counts, /stock/coverage y
        // /stock/discrepancies/resolve son rol 1). El Router no tiene guards de ruta, así
        // que sin esto el encargado entra escribiendo la URL: vería el panel vacío y tres
        // 1002 que en el backoffice se leen como "se venció la sesión". Se lo manda a su
        // home, no al login: la sesión es válida, la pantalla no es para su rol.
        if (!isAdmin(role)) {
            navigateTo(localStorage.getItem('homePageURL') || '/purchase', { replace: true });
            return;
        }

        this.setState({ checked: true, role });
        await Promise.all([this.loadCounts(), this.loadCoverage(), this.loadPendientes()]);
    }

    componentWillUnmount() {
        clearDebouncedCallback(this, 'searchDebounceTimeout');
    }

    fetchJson = async (path, options) => {
        const res = await fetch(apiBase() + path, { headers: authHeaders(), ...options });
        return res.json();
    }

    loadCounts = async ({
        page = this.state.currentPage,
        limit = this.state.quantityShowRows,
        search = this.state.search,
        onlyDiscrepancies = this.state.onlyDiscrepancies,
    } = {}) => {
        const query = buildPaginationQuery({
            page, limit, search,
            extraParams: onlyDiscrepancies ? { result: 2 } : {},
        });
        const body = await this.fetchJson('stock/counts?' + query, { method: 'GET' });
        if (body.error !== 0) return;
        // `data` es el array y la paginación viaja como campo hermano (ENI-254): no está dentro
        // de `data`, así que `getPaginatedData` no aplica acá.
        const pagination = body.pagination || {};
        this.setState({
            counts: body.data || [],
            currentPage: pagination.page || Number(page),
            quantityShowRows: pagination.limit || Number(limit),
            totalItems: pagination.total || 0,
            search,
            onlyDiscrepancies,
        });
    }

    // Las discrepancias que siguen esperando al control, contadas por el servidor sobre TODO el
    // histórico. Es el número de la tarjeta: si saliera de la página cargada, cambiaría al
    // paginar y al buscar.
    loadPendientes = async () => {
        const body = await this.fetchJson('stock/counts?result=2&resolution=pending&limit=1', { method: 'GET' });
        if (body.error === 0) this.setState({ pendientes: (body.pagination || {}).total || 0 });
    }

    loadCoverage = async () => {
        const body = await this.fetchJson('stock/coverage', { method: 'GET' });
        if (body.error === 0) this.setState({ coverage: body.data });
    }

    toggleDiscrepancies = () => {
        // Vuelve a la página 1: quedarse en la 7 con un filtro nuevo muestra una página vacía y
        // se lee como "no hay nada".
        this.loadCounts({ page: 1, onlyDiscrepancies: !this.state.onlyDiscrepancies });
    }

    onChangeSearch = (search) => {
        this.setState({ searchInput: search });
        scheduleDebouncedCallback(this, 'searchDebounceTimeout', () => {
            this.loadCounts({ page: 1, search });
        });
    }

    onChangeShowRows = (event) => {
        this.loadCounts({ page: 1, limit: Number(event.target.value) });
    }

    rowKey = (c) => `${c.productCode}|${c.countDate}`

    startResolve = (c, action) => {
        this.setState({ resolving: { key: this.rowKey(c), action }, resolveNote: '' });
    }

    cancelResolve = () => {
        this.setState({ resolving: null, resolveNote: '' });
    }

    // El comentario es obligatorio: ajustar mueve el stock y marcar resuelta cierra el caso;
    // sin el motivo, la fila resuelta no explica nada un mes después. El backend también lo
    // exige (1001), esto es sólo para no hacer viajar un click que ya sabemos que falla.
    resolve = async (productCode, countDate, action) => {
        const note = this.state.resolveNote.trim();
        if (note === '' || this.state.saving) {
            return;
        }
        this.setState({ saving: true });
        try {
            const body = await this.fetchJson('stock/discrepancies/resolve', {
                method: 'POST',
                body: JSON.stringify({ productCode, countDate, action, note }),
            });
            if (body.error === 0) {
                this.setState({ resolving: null, resolveNote: '', saving: false });
                await Promise.all([this.loadCounts(), this.loadPendientes()]);
            } else {
                this.setState({ saving: false });
                alert(body.message || 'No se pudo resolver la discrepancia');
            }
        } catch (e) {
            this.setState({ saving: false });
            alert('No se pudo resolver la discrepancia. Probá de nuevo.');
        }
    }

    renderReasons(raw) {
        if (!raw) return <span className="text-muted">—</span>;
        return raw.split(',').filter(Boolean).map((r, i) => {
            const meta = REASON[r] || { label: r, cls: 'badge-light' };
            return <span key={i} className={`badge ${meta.cls} mr-1`}>{meta.label}</span>;
        });
    }

    // Hubo movimientos (ventas, reposiciones) entre el conteo y hoy. Cuando los hay, "ajustar"
    // NO deja el stock en lo contado, así que el botón deja de prometer eso.
    huboMovimientos = (c) => c.currentStock != null && Number(c.currentStock) !== Number(c.systemStock)

    tituloAjuste = (c) => (this.huboMovimientos(c) ? 'Ajustar por la diferencia' : 'Ajustar al contado')

    // La cuenta escrita en palabras ANTES de confirmar. `adjustTo` lo calcula el backend con la
    // misma función que después aplica el ajuste: acá no se recalcula nada, se muestra.
    explicacionAjuste = (c, ajusta) => {
        const producto = `"${c.name || c.productCode}"`;
        if (!ajusta) {
            const hoy = c.currentStock != null ? c.currentStock : c.systemStock;
            return `— se cierra la discrepancia de ${producto} sin tocar el stock (queda en ${hoy}).`;
        }
        if (c.currentStock == null || c.adjustTo == null) {
            return `— no se pudo leer el stock actual de ${producto}; se va a fijar en ${c.countedQuantity} (lo contado).`;
        }
        const delta = Number(c.adjustDelta);
        const signo = delta > 0 ? `+${delta}` : `${delta}`;
        const clamp = Number(c.currentStock) + delta < 0 ? ' (no puede quedar negativo)' : '';
        if (!this.huboMovimientos(c)) {
            return `— el conteo encontró ${c.countedQuantity} donde el sistema decía ${c.systemStock} (${signo}).`
                + ` No hubo movimientos desde el conteo: el stock de ${producto} queda en ${c.adjustTo}${clamp}.`;
        }
        return `— el conteo encontró ${c.countedQuantity} donde el sistema decía ${c.systemStock} (${signo}).`
            + ` Desde entonces hubo movimientos: hoy hay ${c.currentStock}.`
            + ` Se aplica la misma diferencia: ${producto} queda en ${c.adjustTo}${clamp}, no en ${c.countedQuantity}.`;
    }

    // Confirmación de la resolución, en una fila propia debajo de la discrepancia: el
    // comentario necesita ancho para escribirse y para leerse, y la última columna de una tabla
    // de 8 no lo tiene. Queda al lado del dato que explica, no en un modal aparte.
    renderResolveForm(c, action) {
        const ajusta = action === 'adjust';
        const note = this.state.resolveNote;
        return (
            <tr className={ajusta ? 'table-warning' : 'table-light'}>
                <td colSpan="8">
                    <div className="p-2">
                        <strong>{ajusta ? this.tituloAjuste(c) : 'Marcar resuelta'}</strong>{' '}
                        <span className="text-muted">{this.explicacionAjuste(c, ajusta)}</span>
                        <textarea
                            className="form-control mt-2"
                            rows="2"
                            maxLength={500}
                            autoFocus
                            placeholder="¿Por qué? (obligatorio) Ej.: dos unidades estaban en la vidriera; se repuso sin cargar la reposición…"
                            value={note}
                            onChange={(e) => this.setState({ resolveNote: e.target.value })}
                        />
                        <div className="mt-2">
                            <button
                                type="button"
                                className={'btn btn-sm mr-2 ' + (ajusta ? 'btn-success' : 'btn-secondary')}
                                disabled={note.trim() === '' || this.state.saving}
                                onClick={() => this.resolve(c.productCode, c.countDate, action)}
                            >
                                {this.state.saving ? 'Guardando…' : 'Confirmar'}
                            </button>
                            <button type="button" className="btn btn-sm btn-outline-secondary" onClick={this.cancelResolve}>Cancelar</button>
                            {note.trim() === '' && <small className="text-muted ml-2">Escribí el motivo para poder confirmar.</small>}
                        </div>
                    </div>
                </td>
            </tr>
        );
    }

    statCard(title, value, sub, danger) {
        return (
            <div className="card card-small">
                <div className="card-body text-center">
                    <h6 className="text-uppercase text-muted mb-1" style={{ fontSize: '.7rem' }}>{title}</h6>
                    <div style={{ fontSize: '2rem', fontWeight: 600 }} className={danger ? 'text-danger' : ''}>{value}</div>
                    <small className="text-muted">{sub}</small>
                </div>
            </div>
        );
    }

    render() {
        if (!this.state.checked) {
            return (<div></div>);
        }
        const { counts, coverage, onlyDiscrepancies, pendientes } = this.state;
        return (
            <div className="container-fluid">
                <div className="row">
                    <SidebarComponent itemSelected={24} role={this.state.role} />
                    <main className="main-content col-lg-10 col-md-9 col-sm-12 p-0 offset-lg-2 offset-md-3">
                        <NavbarComponent role={this.state.role} />
                        <div className="main-content-container container-fluid px-4 mb-4">
                            <HeaderComponent title="Productos" subtitle="Conteo — control" />

                            <div className="row">
                                <div className="col-md-4 mb-4">
                                    {this.statCard(
                                        `Cobertura ${coverage ? `(${coverage.windowDays}d)` : ''}`,
                                        coverage ? `${coverage.coveragePct}%` : '—',
                                        coverage ? `${coverage.counted}/${coverage.total} productos` : '',
                                        false,
                                    )}
                                </div>
                                <div className="col-md-4 mb-4">
                                    {this.statCard('Discrepancias pendientes', pendientes, 'por resolver', pendientes > 0)}
                                </div>
                            </div>

                            <StockCountPaceComponent />

                            <div className="card card-small mb-4">
                                <div className="card-header border-bottom d-flex justify-content-between align-items-center flex-wrap">
                                    <h6 className="m-0 mr-3">Conteos</h6>
                                    <div className="d-flex align-items-center flex-wrap">
                                        <label className="m-0 mr-3">
                                            <input type="checkbox" checked={onlyDiscrepancies} onChange={this.toggleDiscrepancies} /> Solo discrepancias
                                        </label>
                                        <label className="m-0 mr-3">
                                            <span className="mr-1">Filas</span>
                                            <select
                                                className="custom-select custom-select-sm"
                                                style={{ width: 'auto', display: 'inline-block' }}
                                                value={this.state.quantityShowRows}
                                                onChange={this.onChangeShowRows}
                                            >
                                                <option value="50">50</option>
                                                <option value="100">100</option>
                                                <option value="200">200</option>
                                                <option value="500">500</option>
                                            </select>
                                        </label>
                                        {/* La búsqueda la resuelve el SERVIDOR: filtrar en el browser
                                            sólo alcanzaría la página cargada, así que buscar un juego
                                            de la página 3 diría "no hay resultados". */}
                                        <label className="m-0">
                                            <input
                                                type="search"
                                                className="form-control form-control-sm"
                                                style={{ minWidth: 220 }}
                                                placeholder="Buscar juego (nombre o código)"
                                                value={this.state.searchInput}
                                                onChange={(e) => this.onChangeSearch(e.target.value)}
                                            />
                                        </label>
                                    </div>
                                </div>
                                <div className="card-body p-0 pb-3">
                                    <table className="table mb-0">
                                        <thead className="bg-light">
                                            <tr>
                                                <th className="border-0">Producto</th>
                                                <th className="border-0">Por qué se eligió</th>
                                                <th className="border-0">Contado</th>
                                                <th className="border-0">Sistema</th>
                                                <th className="border-0">Resultado</th>
                                                <th className="border-0">Intentos</th>
                                                <th className="border-0">Quién / cuándo</th>
                                                <th className="border-0">Resolución</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {counts.map((c) => {
                                                const res = RESULT[c.result] || { label: c.result, cls: 'badge-light' };
                                                const key = this.rowKey(c);
                                                const resolving = this.state.resolving;
                                                const abierta = resolving != null && resolving.key === key;
                                                return (
                                                    // Key por producto+fecha (la PK), no por índice: con el filtro de
                                                    // "solo discrepancias" el índice cambia de fila y el formulario
                                                    // abierto quedaría pegado a otra discrepancia.
                                                    <React.Fragment key={key}>
                                                    <tr>
                                                        <td>
                                                            {/* Al histórico de ese juego: la pregunta que sigue a una
                                                                discrepancia es siempre "¿y qué pasó con el stock de
                                                                este?".

                                                                `Link` y no un `btn btn-link`: la clase `.btn` trae su
                                                                propia tipografía (12px/300, line-height 13.5) y
                                                                `vertical-align: middle`, así que el nombre salía más
                                                                chico y más finito que el resto de la tabla y
                                                                desalineado con su fila. Un ancla hereda la tipografía
                                                                de la celda y encima se puede abrir en otra pestaña. */}
                                                            {/* `color: inherit`: una columna entera de nombres pintada
                                                                de azul no informa nada — que es clickeable ya lo dicen el
                                                                cursor y el subrayado al pasar por encima. Heredado y no
                                                                un color fijo, para que siga al tema si cambia. */}
                                                            <Link
                                                                to={`/stockHistory?code=${c.productCode}`}
                                                                title="Ver el histórico de movimientos de stock"
                                                                style={{ color: 'inherit' }}
                                                            >
                                                                {c.name || c.productCode}
                                                            </Link>
                                                        </td>
                                                        <td>{this.renderReasons(c.selectionReasons)}</td>
                                                        <td>{c.countedQuantity}</td>
                                                        <td>
                                                            {c.systemStock}
                                                            {/* El stock se movió desde el conteo: es lo que hace que
                                                                "ajustar al contado" ya no sea lo correcto. */}
                                                            {this.huboMovimientos(c) && (
                                                                <div><small className="text-muted">hoy: {c.currentStock}</small></div>
                                                            )}
                                                        </td>
                                                        <td>
                                                            <span className={`badge ${res.cls}`}>{res.label}</span>
                                                            {/* El motivo que dejó el operador: lo único que explica la
                                                                diferencia sin ir a preguntarle. */}
                                                            {c.countNote && (
                                                                <div><small className="text-muted" title={c.countNote}>«{c.countNote}»</small></div>
                                                            )}
                                                        </td>
                                                        <td>
                                                            {c.attempts && c.attempts.length > 0
                                                                ? <span className={c.attempts.length > 1 ? 'badge badge-warning' : ''}>{c.attempts.map((a) => a.counted).join(' → ')}</span>
                                                                : '—'}
                                                        </td>
                                                        <td>
                                                            {/* Los nombres primero: `countedBy` es el usuario del
                                                                mostrador, que comparten varias personas. */}
                                                            {c.responsibleNames
                                                                ? <small><strong>{c.responsibleNames}</strong></small>
                                                                : <small className="text-muted">Sin responsable anotado</small>}
                                                            <br /><small className="text-muted">{c.countedBy}</small>
                                                            <br /><small className="text-muted">{formatEventDateTime(c.countedAt)}</small>
                                                        </td>
                                                        <td>
                                                            {Number(c.result) !== 2 ? '—'
                                                                : Number(c.resolutionStatus) === 2
                                                                    ? (
                                                                        <span className="text-success">
                                                                            Resuelta{c.resolutionDelta != null && ` (${Number(c.resolutionDelta) > 0 ? '+' : ''}${c.resolutionDelta})`}
                                                                            <br /><small>{c.resolvedBy}</small>
                                                                            {c.resolutionNote && (
                                                                                <div><small className="text-muted" title={c.resolutionNote}>«{c.resolutionNote}»</small></div>
                                                                            )}
                                                                        </span>
                                                                    )
                                                                    : (
                                                                        <span>
                                                                            <button type="button" className="btn btn-sm btn-success mr-1 mb-1" onClick={() => this.startResolve(c, 'adjust')}>{this.tituloAjuste(c)}</button>
                                                                            <button type="button" className="btn btn-sm btn-outline-secondary mb-1" onClick={() => this.startResolve(c, 'mark')}>Marcar resuelta</button>
                                                                        </span>
                                                                    )}
                                                        </td>
                                                    </tr>
                                                    {abierta && this.renderResolveForm(c, resolving.action)}
                                                    </React.Fragment>
                                                );
                                            })}
                                            {counts.length === 0 && (
                                                <tr>
                                                    <td colSpan="8" className="text-center text-muted p-3">
                                                        {this.state.search
                                                            ? `No hay conteos para "${this.state.search}".`
                                                            : 'Todavía no hay conteos registrados.'}
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                                <div className="card-footer">
                                    <PaginationComponent
                                        totalItems={this.state.totalItems}
                                        quantityRows={this.state.quantityShowRows}
                                        currentPage={this.state.currentPage}
                                        onPageChange={(page) => this.loadCounts({ page })}
                                    />
                                </div>
                            </div>
                        </div>
                    </main>
                </div>
            </div>
        );
    }
}

export default StockCountAdminPage;
